// Session summaries, ranking, and validation metrics against user labels.

import type { AttemptAnalysis, Outcome } from './analysis';
import { median, spread, type Vec2 } from './geometry';
import type { AnalysisParams } from './params';

export interface SessionStats {
  total: number;
  counts: Record<Outcome, number>;
  /** Attempts that produced a verdict (clean or adjust). */
  assessed: number;
  cleanRate: number | null;
  medianCleanPopupMs: number | null;
  /** RMS scatter of initial foot positions across assessed attempts, cm. */
  spread: { front: number; rear: number } | null;
  /** Mean initial error, cm (along: + toward nose; across: + toward toes). */
  bias: { front: Vec2; rear: Vec2 } | null;
  /** Most frequent problem among attempts needing adjustment. */
  commonIssue: { key: IssueKey; count: number } | null;
}

export type IssueKey =
  | 'frontBehind' | 'frontAhead' | 'frontToes' | 'frontHeels'
  | 'rearBehind' | 'rearAhead' | 'rearToes' | 'rearHeels'
  | 'narrow' | 'wide' | 'stepped' | 'noHold';

export const ISSUE_KEY_TEXT: Record<IssueKey, string> = {
  frontBehind: 'Front foot landing behind target',
  frontAhead: 'Front foot landing ahead of target',
  frontToes: 'Front foot too far toward your toes',
  frontHeels: 'Front foot too far toward your heels',
  rearBehind: 'Back foot landing behind target',
  rearAhead: 'Back foot landing ahead of target',
  rearToes: 'Back foot too far toward your toes',
  rearHeels: 'Back foot too far toward your heels',
  narrow: 'Feet landing closer together than your stance',
  wide: 'Stance wider than your target',
  stepped: 'Stepping after landing',
  noHold: 'Not holding the landing',
};

/** The specific problems behind an "adjust" outcome. */
export function issueKeys(a: AttemptAnalysis, P: AnalysisParams): IssueKey[] {
  const keys: IssueKey[] = [];
  const pl = a.initial ?? a.settled;
  if (pl && !pl.onTarget) {
    const fa = pl.front.along / P.tolAlong;
    const ra = pl.rear.along / P.tolAlong;
    if (!pl.front.onTarget && !pl.rear.onTarget && Math.abs(fa) > 1 && Math.abs(ra) > 1 && Math.sign(fa) !== Math.sign(ra)) {
      keys.push(fa < 0 ? 'narrow' : 'wide');
    } else {
      for (const role of ['front', 'rear'] as const) {
        const fp = pl[role];
        if (fp.onTarget) continue;
        const a2 = fp.along / P.tolAlong;
        const c2 = fp.across / P.tolAcross;
        if (Math.abs(a2) >= Math.abs(c2)) keys.push(`${role}${a2 < 0 ? 'Behind' : 'Ahead'}` as IssueKey);
        else keys.push(`${role}${c2 > 0 ? 'Toes' : 'Heels'}` as IssueKey);
      }
    }
  }
  if (a.adjustments.some((x) => x.kind === 'step')) keys.push('stepped');
  if (a.issues.includes('noHold')) keys.push('noHold');
  return keys;
}

const OUTCOME_RANK: Record<Outcome, number> = { clean: 0, adjust: 1, unassessed: 2, none: 3 };

/** Best first: clean & controlled before close misses, then placement accuracy, then speed. */
export function rankAttempts(list: readonly AttemptAnalysis[]): number[] {
  const key = (a: AttemptAnalysis) => [
    OUTCOME_RANK[a.outcome],
    a.adjustments.filter((x) => x.kind === 'step').length,
    a.initial ? a.initial.front.score + a.initial.rear.score : Infinity,
    a.timing.popupMs ?? Infinity,
  ];
  return list
    .map((a, i) => ({ i, k: key(a) }))
    .sort((x, y) => {
      for (let j = 0; j < x.k.length; j++) if (x.k[j] !== y.k[j]) return x.k[j] - y.k[j];
      return x.i - y.i;
    })
    .map((x) => x.i);
}

export function sessionStats(list: readonly AttemptAnalysis[], P: AnalysisParams): SessionStats {
  const counts: Record<Outcome, number> = { clean: 0, adjust: 0, unassessed: 0, none: 0 };
  for (const a of list) counts[a.outcome]++;
  const assessed = counts.clean + counts.adjust;
  const cleanTimes = list.filter((a) => a.outcome === 'clean' && a.timing.popupMs !== null).map((a) => a.timing.popupMs!);
  const placed = list.filter((a) => a.initial && (a.outcome === 'clean' || a.outcome === 'adjust'));
  const tally = new Map<IssueKey, number>();
  for (const a of list) if (a.outcome === 'adjust') for (const k of new Set(issueKeys(a, P))) tally.set(k, (tally.get(k) ?? 0) + 1);
  let commonIssue: SessionStats['commonIssue'] = null;
  for (const [key, count] of tally) if (!commonIssue || count > commonIssue.count) commonIssue = { key, count };
  const avg = (pts: Vec2[]) => ({ x: pts.reduce((s, p) => s + p.x, 0) / pts.length, y: pts.reduce((s, p) => s + p.y, 0) / pts.length });
  return {
    total: list.filter((a) => a.outcome !== 'none').length,
    counts,
    assessed,
    cleanRate: assessed ? counts.clean / assessed : null,
    medianCleanPopupMs: cleanTimes.length ? median(cleanTimes) : null,
    spread:
      placed.length >= 3
        ? { front: spread(placed.map((a) => a.initial!.front.centre)), rear: spread(placed.map((a) => a.initial!.rear.centre)) }
        : null,
    bias: placed.length
      ? {
          front: avg(placed.map((a) => ({ x: a.initial!.front.across, y: a.initial!.front.along }))),
          rear: avg(placed.map((a) => ({ x: a.initial!.rear.across, y: a.initial!.rear.along }))),
        }
      : null,
    commonIssue,
  };
}

export function summarySentence(s: SessionStats): string {
  const n = s.total;
  if (n === 0) return 'No attempts yet.';
  const parts = [`${s.counts.clean} clean landing${s.counts.clean === 1 ? '' : 's'}`];
  if (s.counts.adjust) parts.push(`${s.counts.adjust} needing adjustment`);
  if (s.counts.unassessed) parts.push(`${s.counts.unassessed} not assessed`);
  return `${n} attempt${n === 1 ? '' : 's'}: ${parts.join(', ')}.`;
}

// ---------------------------------------------------------------------------
// Validation against what actually happened (user labels)

export type TruthLabel = 'clean' | 'frontOff' | 'rearOff' | 'bothOff' | 'stepped' | 'hidden' | 'noPopup';

export const TRUTH_TEXT: Record<TruthLabel, string> = {
  clean: 'Clean: landed on target, no shuffle',
  frontOff: 'Front foot landed off target',
  rearOff: 'Back foot landed off target',
  bothOff: 'Both feet landed off target',
  stepped: 'Landed, then stepped or shuffled',
  hidden: 'Feet hidden — can’t tell',
  noPopup: 'Not a real attempt',
};

export const TRUTH_SHORT: Record<TruthLabel, string> = {
  clean: 'Clean',
  frontOff: 'Front off',
  rearOff: 'Back off',
  bothOff: 'Both off',
  stepped: 'Stepped',
  hidden: 'Hidden',
  noPopup: 'Not real',
};

export const EXPECTED_OUTCOME: Record<TruthLabel, Outcome> = {
  clean: 'clean',
  frontOff: 'adjust',
  rearOff: 'adjust',
  bothOff: 'adjust',
  stepped: 'adjust',
  hidden: 'unassessed',
  noPopup: 'none',
};

export interface ValidationReport {
  labelled: number;
  /** Outcome agreement with the label, over labelled attempts. */
  agreement: number | null;
  /** Share of labelled real attempts (not hidden/noPopup) that received a verdict. */
  coverage: number | null;
  /** Of real attempts given a verdict, share that matched the label. */
  accuracyWhenAssessed: number | null;
  /** Corrected landings wrongly scored clean — the error that matters most. */
  correctedScoredClean: number;
  /** For off-target labels, share where the app blamed the right foot. */
  rightFoot: number | null;
  confusion: Record<TruthLabel, Record<Outcome, number>>;
}

export function validationReport(items: readonly { label: TruthLabel; analysis: AttemptAnalysis }[]): ValidationReport {
  const outcomes: Outcome[] = ['clean', 'adjust', 'unassessed', 'none'];
  const confusion = Object.fromEntries(
    (Object.keys(TRUTH_TEXT) as TruthLabel[]).map((l) => [l, Object.fromEntries(outcomes.map((o) => [o, 0]))]),
  ) as ValidationReport['confusion'];
  let agree = 0;
  let real = 0;
  let realAssessed = 0;
  let realAssessedRight = 0;
  let correctedScoredClean = 0;
  let footLabels = 0;
  let footRight = 0;
  for (const { label, analysis: a } of items) {
    confusion[label][a.outcome]++;
    if (a.outcome === EXPECTED_OUTCOME[label]) agree++;
    if (label !== 'hidden' && label !== 'noPopup') {
      real++;
      if (a.outcome === 'clean' || a.outcome === 'adjust') {
        realAssessed++;
        if (a.outcome === EXPECTED_OUTCOME[label]) realAssessedRight++;
      }
      if (label === 'stepped' && a.outcome === 'clean') correctedScoredClean++;
    }
    if ((label === 'frontOff' || label === 'rearOff' || label === 'bothOff') && a.initial) {
      footLabels++;
      const f = !a.initial.front.onTarget;
      const r = !a.initial.rear.onTarget;
      if ((label === 'frontOff' && f && !r) || (label === 'rearOff' && r && !f) || (label === 'bothOff' && f && r)) footRight++;
    }
  }
  const n = items.length;
  return {
    labelled: n,
    agreement: n ? agree / n : null,
    coverage: real ? realAssessed / real : null,
    accuracyWhenAssessed: realAssessed ? realAssessedRight / realAssessed : null,
    correctedScoredClean,
    rightFoot: footLabels ? footRight / footLabels : null,
    confusion,
  };
}
